import React, { useState, useEffect, useCallback } from 'react';
import { exportApi, vehicleRevenueApi, vehicleCostApi, vehicleApi } from '../services/api';
import { 
  FileArrowDown, FilePdf, Car, Users, Calendar, Receipt, 
  Presentation, CaretLeft, CaretRight, ChartLineUp, Wrench, CaretDown, CaretUp
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const formatEur = (v) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const monthLabel = (yyyymm) => {
  if (!yyyymm || yyyymm.length < 7) return yyyymm;
  const m = parseInt(yyyymm.slice(5, 7), 10);
  return `${MONTHS_SHORT[m - 1]} ${yyyymm.slice(2, 4)}`;
};

const Reports = () => {
  const now = new Date();
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [reportYear, setReportYear] = useState(now.getFullYear());

  // Vehicle revenue state
  const currentYear = now.getFullYear();
  const [vrFrom, setVrFrom] = useState(`${currentYear}-01`);
  const [vrTo, setVrTo] = useState(`${currentYear}-12`);
  const [vrVehicleId, setVrVehicleId] = useState('all');
  const [vrVehicles, setVrVehicles] = useState([]);
  const [vrData, setVrData] = useState(null);
  const [vrLoading, setVrLoading] = useState(false);

  // Vehicle cost analysis state
  const [vcFrom, setVcFrom] = useState(`${currentYear}-01`);
  const [vcTo, setVcTo] = useState(`${currentYear}-12`);
  const [vcVehicleId, setVcVehicleId] = useState('all');
  const [vcData, setVcData] = useState(null);
  const [vcLoading, setVcLoading] = useState(false);
  const [vcExpandedRow, setVcExpandedRow] = useState(null);

  useEffect(() => {
    vehicleApi.getAll().then((r) => setVrVehicles(r.data || [])).catch(() => {});
  }, []);

  const loadVehicleRevenue = useCallback(async () => {
    setVrLoading(true);
    try {
      const res = await vehicleRevenueApi.get(vrFrom, vrTo, vrVehicleId === 'all' ? null : vrVehicleId);
      setVrData(res.data);
    } catch (_) {
      toast.error('Fahrzeug-Umsatzdaten konnten nicht geladen werden');
    } finally {
      setVrLoading(false);
    }
  }, [vrFrom, vrTo, vrVehicleId]);

  useEffect(() => { loadVehicleRevenue(); }, [loadVehicleRevenue]);

  const loadVehicleCost = useCallback(async () => {
    setVcLoading(true);
    try {
      const res = await vehicleCostApi.get(vcFrom, vcTo, vcVehicleId === 'all' ? null : vcVehicleId);
      setVcData(res.data);
    } catch (_) {
      toast.error('Kostenanalyse konnte nicht geladen werden');
    } finally {
      setVcLoading(false);
    }
  }, [vcFrom, vcTo, vcVehicleId]);

  useEffect(() => { loadVehicleCost(); }, [loadVehicleCost]);

  const downloadCsv = async (url, filename) => {
    try {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success('Download gestartet');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const openPdf = async (url, filename) => {
    try {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
      toast.success('PDF erstellt');
    } catch (error) {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const prevMonth = () => {
    if (reportMonth === 1) {
      setReportMonth(12);
      setReportYear(reportYear - 1);
    } else {
      setReportMonth(reportMonth - 1);
    }
  };

  const nextMonth = () => {
    if (reportMonth === 12) {
      setReportMonth(1);
      setReportYear(reportYear + 1);
    } else {
      setReportMonth(reportMonth + 1);
    }
  };

  const exports = [
    {
      title: 'Fahrzeugliste',
      description: 'Alle Fahrzeuge mit Details (Marke, Modell, Kennzeichen, Status, Tagespreis)',
      icon: Car,
      color: '#E60000',
      onCsv: () => downloadCsv(exportApi.vehicles(), `fahrzeuge_${new Date().toISOString().slice(0,10)}.csv`),
      onPdf: () => openPdf(exportApi.pdfVehicles(), `fahrzeuge_${new Date().toISOString().slice(0,10)}.pdf`),
    },
    {
      title: 'Kundenliste',
      description: 'Alle Kunden mit Kontaktdaten und Führerscheininformationen',
      icon: Users,
      color: '#0A0A0A',
      onCsv: () => downloadCsv(exportApi.customers(), `kunden_${new Date().toISOString().slice(0,10)}.csv`),
      onPdf: () => openPdf(exportApi.pdfCustomers(), `kunden_${new Date().toISOString().slice(0,10)}.pdf`),
    },
    {
      title: 'Buchungsliste',
      description: 'Alle Buchungen mit Kunde, Fahrzeug, Zeitraum und Preis',
      icon: Calendar,
      color: '#009951',
      onCsv: () => downloadCsv(exportApi.bookings(), `buchungen_${new Date().toISOString().slice(0,10)}.csv`),
      onPdf: () => openPdf(exportApi.pdfBookings(), `buchungen_${new Date().toISOString().slice(0,10)}.pdf`),
    },
    {
      title: 'Rechnungsliste',
      description: 'Alle Rechnungen mit Beträgen und Zahlungsstatus',
      icon: Receipt,
      color: '#FFB800',
      onCsv: () => downloadCsv(exportApi.invoices(), `rechnungen_${new Date().toISOString().slice(0,10)}.csv`),
      onPdf: () => openPdf(exportApi.pdfInvoices(), `rechnungen_${new Date().toISOString().slice(0,10)}.pdf`),
    }
  ];

  return (
    <div className="p-6 space-y-8" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Berichte & Export</h1>
        <p className="text-[#525866] mt-1">Daten als CSV exportieren und Berichte generieren</p>
      </div>

      {/* Revenue Report */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <Presentation weight="fill" size={24} className="text-[#E60000]" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Umsatz- & Auslastungsbericht</h2>
            <p className="text-sm text-[#525866]">Monatlicher Bericht mit Umsatz, Buchungen und Flottenauslastung</p>
          </div>
        </div>

        <div className="flex items-center gap-6 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={prevMonth} className="p-2" data-testid="report-prev-month">
              <CaretLeft weight="bold" size={18} />
            </Button>
            <span className="text-lg font-semibold min-w-[180px] text-center">
              {MONTHS[reportMonth - 1]} {reportYear}
            </span>
            <Button variant="ghost" onClick={nextMonth} className="p-2" data-testid="report-next-month">
              <CaretRight weight="bold" size={18} />
            </Button>
          </div>
        </div>

        <Button
          onClick={() => openPdf(
            exportApi.pdfRevenueReport(reportMonth, reportYear),
            `umsatzbericht_${reportYear}_${String(reportMonth).padStart(2, '0')}.pdf`
          )}
          className="btn-primary px-6 py-3 mr-2"
          data-testid="download-revenue-report-pdf"
        >
          <FilePdf weight="bold" size={18} className="mr-2" />
          Bericht als PDF
        </Button>
        <Button
          onClick={() => downloadCsv(
            exportApi.revenueReport(reportMonth, reportYear),
            `umsatzbericht_${reportYear}_${String(reportMonth).padStart(2, '0')}.csv`
          )}
          variant="outline"
          className="px-6 py-3"
          data-testid="download-revenue-report"
        >
          <FileArrowDown weight="bold" size={18} className="mr-2" />
          Als CSV
        </Button>
      </div>

      {/* Vehicle Revenue Report (per Fahrzeug × Monat) */}
      <div className="card-swiss p-6" data-testid="vehicle-revenue-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#009951]/10 rounded-sm flex items-center justify-center">
            <ChartLineUp weight="fill" size={24} className="text-[#009951]" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Fahrzeug-Umsatz nach Monat</h2>
            <p className="text-sm text-[#525866]">Soll (gebucht) vs. Ist (bezahlt) pro Fahrzeug & Monat – frei wählbarer Zeitraum</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div>
            <Label className="overline mb-1 block">Von (Monat)</Label>
            <Input
              type="month"
              value={vrFrom}
              onChange={(e) => setVrFrom(e.target.value)}
              className="w-44"
              data-testid="vr-from-input"
            />
          </div>
          <div>
            <Label className="overline mb-1 block">Bis (Monat)</Label>
            <Input
              type="month"
              value={vrTo}
              onChange={(e) => setVrTo(e.target.value)}
              className="w-44"
              data-testid="vr-to-input"
            />
          </div>
          <div className="min-w-[260px]">
            <Label className="overline mb-1 block">Fahrzeug</Label>
            <Select value={vrVehicleId} onValueChange={setVrVehicleId}>
              <SelectTrigger data-testid="vr-vehicle-select"><SelectValue placeholder="Alle Fahrzeuge" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Alle Fahrzeuge</SelectItem>
                {vrVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.brand} {v.model} ({v.license_plate})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => openPdf(
              exportApi.pdfVehicleRevenue(vrFrom, vrTo, vrVehicleId === 'all' ? null : vrVehicleId, true),
              `fahrzeug_umsatz_${vrFrom}_${vrTo}.pdf`
            )}
            className="btn-primary"
            data-testid="vr-download-pdf"
          >
            <FilePdf weight="bold" size={16} className="mr-2" />
            Als PDF (mit Detailliste)
          </Button>
        </div>

        {vrLoading && <p className="text-sm text-[#525866]">Lade Daten…</p>}

        {!vrLoading && vrData && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Soll gesamt</p>
                <p className="text-xl font-bold text-[#111418]" data-testid="vr-grand-soll">{formatEur(vrData.grand_total.soll)}</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Ist gesamt</p>
                <p className="text-xl font-bold text-[#009951]" data-testid="vr-grand-ist">{formatEur(vrData.grand_total.ist)}</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Buchungen</p>
                <p className="text-xl font-bold text-[#111418]">{vrData.grand_total.bookings}</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Differenz</p>
                <p className={`text-xl font-bold ${(vrData.grand_total.ist - vrData.grand_total.soll) < 0 ? 'text-[#E60000]' : 'text-[#009951]'}`}>
                  {formatEur(vrData.grand_total.ist - vrData.grand_total.soll)}
                </p>
              </div>
            </div>

            {/* Matrix */}
            <div className="overflow-x-auto border border-[#E2E4E9] rounded-sm">
              <table className="w-full text-sm" data-testid="vr-matrix-table">
                <thead className="bg-[#111418] text-white">
                  <tr>
                    <th className="text-left p-2 font-semibold sticky left-0 bg-[#111418] z-10 min-w-[180px]">Fahrzeug</th>
                    {vrData.months.map((m) => (
                      <th key={m} className="text-right p-2 font-semibold min-w-[110px]">{monthLabel(m)}</th>
                    ))}
                    <th className="text-right p-2 font-semibold min-w-[120px]">Summe</th>
                  </tr>
                </thead>
                <tbody>
                  {vrData.vehicles.map((v) => (
                    <tr key={v.vehicle_id} className="border-t border-[#E2E4E9] even:bg-[#F7F8FA]">
                      <td className="p-2 sticky left-0 bg-inherit">
                        <div className="font-semibold text-[#111418]">{v.brand_model || '—'}</div>
                        <div className="text-xs text-[#8A94A6] font-mono">{v.license_plate}</div>
                      </td>
                      {vrData.months.map((m) => {
                        const cell = v.monthly[m] || { soll: 0, ist: 0 };
                        const empty = !cell.soll && !cell.ist;
                        return (
                          <td key={m} className="p-2 text-right tabular-nums">
                            {empty ? (
                              <span className="text-[#C0C5CD]">—</span>
                            ) : (
                              <>
                                <div className="text-[#111418]">{formatEur(cell.soll)}</div>
                                <div className="text-xs text-[#009951]">{formatEur(cell.ist)}</div>
                              </>
                            )}
                          </td>
                        );
                      })}
                      <td className="p-2 text-right tabular-nums font-bold">
                        <div className="text-[#111418]">{formatEur(v.totals.soll)}</div>
                        <div className="text-xs text-[#009951]">{formatEur(v.totals.ist)}</div>
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="border-t-2 border-[#111418] bg-[#F1F2F4] font-bold">
                    <td className="p-2 sticky left-0 bg-[#F1F2F4]">GESAMT</td>
                    {vrData.months.map((m) => {
                      const t = vrData.totals_per_month[m] || { soll: 0, ist: 0 };
                      return (
                        <td key={m} className="p-2 text-right tabular-nums">
                          <div className="text-[#111418]">{formatEur(t.soll)}</div>
                          <div className="text-xs text-[#009951]">{formatEur(t.ist)}</div>
                        </td>
                      );
                    })}
                    <td className="p-2 text-right tabular-nums">
                      <div className="text-[#111418]">{formatEur(vrData.grand_total.soll)}</div>
                      <div className="text-xs text-[#009951]">{formatEur(vrData.grand_total.ist)}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#8A94A6] mt-2">
              <strong>Soll</strong> = vereinbarter Mietpreis (anteilig auf Tage verteilt) • <strong className="text-[#009951]">Ist</strong> = bezahlte Rechnungen (Zuordnung nach Rechnungsdatum)
            </p>
          </>
        )}
      </div>

      {/* Vehicle Cost Analysis */}
      <div className="card-swiss p-6" data-testid="vehicle-cost-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#FFB800]/10 rounded-sm flex items-center justify-center">
            <Wrench weight="fill" size={24} className="text-[#FFB800]" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Fahrzeug-Kostenanalyse</h2>
            <p className="text-sm text-[#525866]">Werkstattrechnungen + Schadenskosten gegenüber Mieteinnahmen – welches Fahrzeug kostet am meisten?</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div>
            <Label className="overline mb-1 block">Von (Monat)</Label>
            <Input
              type="month"
              value={vcFrom}
              onChange={(e) => setVcFrom(e.target.value)}
              className="w-44"
              data-testid="vc-from-input"
            />
          </div>
          <div>
            <Label className="overline mb-1 block">Bis (Monat)</Label>
            <Input
              type="month"
              value={vcTo}
              onChange={(e) => setVcTo(e.target.value)}
              className="w-44"
              data-testid="vc-to-input"
            />
          </div>
          <div className="min-w-[260px]">
            <Label className="overline mb-1 block">Fahrzeug</Label>
            <Select value={vcVehicleId} onValueChange={setVcVehicleId}>
              <SelectTrigger data-testid="vc-vehicle-select"><SelectValue placeholder="Alle Fahrzeuge" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Alle Fahrzeuge</SelectItem>
                {vrVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.brand} {v.model} ({v.license_plate})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {vcLoading && <p className="text-sm text-[#525866]">Lade Kostenanalyse…</p>}

        {!vcLoading && vcData && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Umsatz (bezahlt)</p>
                <p className="text-xl font-bold text-[#009951]" data-testid="vc-grand-revenue">{formatEur(vcData.grand_total.revenue)}</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Werkstatt-Kosten</p>
                <p className="text-xl font-bold text-[#FFB800]" data-testid="vc-grand-workshop">{formatEur(vcData.grand_total.workshop_cost)}</p>
                <p className="text-xs text-[#8A94A6] mt-0.5">{vcData.grand_total.workshop_count} Rechnungen</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Schaden-Kosten</p>
                <p className="text-xl font-bold text-[#E60000]" data-testid="vc-grand-damage">{formatEur(vcData.grand_total.damage_cost)}</p>
                <p className="text-xs text-[#8A94A6] mt-0.5">{vcData.grand_total.damage_count} Schäden</p>
              </div>
              <div className="card-swiss p-3 bg-[#F7F8FA]">
                <p className="overline text-[#8A94A6]">Gewinn / Verlust</p>
                <p className={`text-xl font-bold ${vcData.grand_total.profit < 0 ? 'text-[#E60000]' : 'text-[#009951]'}`} data-testid="vc-grand-profit">
                  {formatEur(vcData.grand_total.profit)}
                </p>
                <p className="text-xs text-[#8A94A6] mt-0.5">Kostenquote: {vcData.grand_total.cost_ratio_pct}%</p>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-[#E2E4E9] rounded-sm">
              <table className="w-full text-sm" data-testid="vc-table">
                <thead className="bg-[#111418] text-white">
                  <tr>
                    <th className="text-left p-2 font-semibold min-w-[200px]">Fahrzeug</th>
                    <th className="text-right p-2 font-semibold">Umsatz</th>
                    <th className="text-right p-2 font-semibold">Werkstatt</th>
                    <th className="text-right p-2 font-semibold">Schäden</th>
                    <th className="text-right p-2 font-semibold">Kosten gesamt</th>
                    <th className="text-right p-2 font-semibold">Gewinn</th>
                    <th className="text-right p-2 font-semibold">Kostenquote</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {vcData.vehicles.length === 0 && (
                    <tr><td colSpan={8} className="p-6 text-center text-[#8A94A6]">Keine Daten im gewählten Zeitraum</td></tr>
                  )}
                  {vcData.vehicles.map((v) => {
                    const ratio = v.cost_ratio_pct;
                    let ratioColor = 'text-[#009951]';
                    let ratioBg = 'bg-[#009951]/10';
                    if (ratio >= 100) { ratioColor = 'text-white'; ratioBg = 'bg-[#E60000]'; }
                    else if (ratio >= 50) { ratioColor = 'text-[#E60000]'; ratioBg = 'bg-[#E60000]/10'; }
                    else if (ratio >= 30) { ratioColor = 'text-[#FFB800]'; ratioBg = 'bg-[#FFB800]/15'; }
                    const isOpen = vcExpandedRow === v.vehicle_id;
                    const hasDetails = (v.workshop_items?.length || 0) + (v.damage_items?.length || 0) > 0;
                    return (
                      <React.Fragment key={v.vehicle_id}>
                        <tr
                          className={`border-t border-[#E2E4E9] even:bg-[#F7F8FA] ${hasDetails ? 'cursor-pointer hover:bg-[#F1F2F4]' : ''}`}
                          onClick={() => hasDetails && setVcExpandedRow(isOpen ? null : v.vehicle_id)}
                          data-testid={`vc-row-${v.vehicle_id}`}
                        >
                          <td className="p-2">
                            <div className="font-semibold text-[#111418]">{v.brand_model || '—'}</div>
                            <div className="text-xs text-[#8A94A6] font-mono">{v.license_plate}</div>
                          </td>
                          <td className="p-2 text-right tabular-nums text-[#009951]">{formatEur(v.revenue)}</td>
                          <td className="p-2 text-right tabular-nums">
                            <div className="text-[#111418]">{formatEur(v.workshop_cost)}</div>
                            {v.workshop_count > 0 && <div className="text-xs text-[#8A94A6]">{v.workshop_count} Rg.</div>}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            <div className="text-[#111418]">{formatEur(v.damage_cost)}</div>
                            {v.damage_count > 0 && <div className="text-xs text-[#8A94A6]">{v.damage_count} Schäden</div>}
                          </td>
                          <td className="p-2 text-right tabular-nums font-semibold text-[#111418]">{formatEur(v.total_cost)}</td>
                          <td className={`p-2 text-right tabular-nums font-bold ${v.profit < 0 ? 'text-[#E60000]' : 'text-[#009951]'}`}>{formatEur(v.profit)}</td>
                          <td className="p-2 text-right">
                            <span className={`inline-block px-2 py-1 rounded-sm text-xs font-bold tabular-nums ${ratioBg} ${ratioColor}`}>
                              {ratio}%
                            </span>
                          </td>
                          <td className="p-2 text-center text-[#8A94A6]">
                            {hasDetails && (isOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />)}
                          </td>
                        </tr>
                        {isOpen && hasDetails && (
                          <tr className="bg-[#FAFBFC]">
                            <td colSpan={8} className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-sm font-bold mb-2 text-[#FFB800] flex items-center gap-2">
                                    <Wrench size={14} weight="bold" /> Werkstattrechnungen ({v.workshop_count})
                                  </h4>
                                  {v.workshop_items.length === 0 ? (
                                    <p className="text-xs text-[#8A94A6]">Keine Werkstatt-Einträge</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {v.workshop_items.map((it) => (
                                        <div key={it.block_id} className="flex justify-between items-center text-xs border-b border-[#E2E4E9] py-1">
                                          <div className="flex-1">
                                            <div className="font-medium text-[#111418]">{it.supplier || '—'}</div>
                                            <div className="text-[#8A94A6]">
                                              {it.invoice_date?.slice(0,10) || '—'}
                                              {it.invoice_number ? ` • Rg-Nr.: ${it.invoice_number}` : ''}
                                            </div>
                                          </div>
                                          <div className="font-mono font-semibold text-[#111418] tabular-nums">{formatEur(it.amount)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <h4 className="text-sm font-bold mb-2 text-[#E60000] flex items-center gap-2">
                                    <Receipt size={14} weight="bold" /> Schäden ({v.damage_count})
                                  </h4>
                                  {v.damage_items.length === 0 ? (
                                    <p className="text-xs text-[#8A94A6]">Keine Schäden-Einträge</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {v.damage_items.map((it) => (
                                        <div key={it.damage_id} className="flex justify-between items-center text-xs border-b border-[#E2E4E9] py-1">
                                          <div className="flex-1">
                                            <div className="font-medium text-[#111418]">
                                              {it.damage_number || it.damage_type || 'Schaden'}
                                            </div>
                                            <div className="text-[#8A94A6]">
                                              {(it.reported_date || '').slice(0,10)} • {it.description || '—'}
                                            </div>
                                          </div>
                                          <div className="font-mono font-semibold text-[#111418] tabular-nums">{formatEur(it.amount)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Totals row */}
                  {vcData.vehicles.length > 0 && (
                    <tr className="border-t-2 border-[#111418] bg-[#F1F2F4] font-bold">
                      <td className="p-2">GESAMT ({vcData.grand_total.vehicles} Fzg.)</td>
                      <td className="p-2 text-right tabular-nums text-[#009951]">{formatEur(vcData.grand_total.revenue)}</td>
                      <td className="p-2 text-right tabular-nums">{formatEur(vcData.grand_total.workshop_cost)}</td>
                      <td className="p-2 text-right tabular-nums">{formatEur(vcData.grand_total.damage_cost)}</td>
                      <td className="p-2 text-right tabular-nums">{formatEur(vcData.grand_total.total_cost)}</td>
                      <td className={`p-2 text-right tabular-nums ${vcData.grand_total.profit < 0 ? 'text-[#E60000]' : 'text-[#009951]'}`}>{formatEur(vcData.grand_total.profit)}</td>
                      <td className="p-2 text-right tabular-nums">{vcData.grand_total.cost_ratio_pct}%</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#8A94A6] mt-2">
              <strong>Kostenquote</strong> = (Werkstatt + Schäden) / Umsatz · Sortiert nach höchsten Gesamtkosten · Zeile anklicken für Details
              <span className="ml-3"><span className="inline-block w-2 h-2 rounded-full bg-[#009951] mr-1"></span>&lt;30%</span>
              <span className="ml-2"><span className="inline-block w-2 h-2 rounded-full bg-[#FFB800] mr-1"></span>30–50%</span>
              <span className="ml-2"><span className="inline-block w-2 h-2 rounded-full bg-[#E60000] mr-1"></span>&ge;50%</span>
            </p>
          </>
        )}
      </div>

      {/* Data Exports */}
      <div>
        <h2 className="text-lg font-bold tracking-tight mb-4">Daten-Export</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exports.map((item) => (
            <div key={item.title} className="card-swiss p-5 flex items-start gap-4 transition-all duration-200">
              <div 
                className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon weight="fill" size={24} style={{ color: item.color }} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#111418] mb-1">{item.title}</h3>
                <p className="text-sm text-[#525866] mb-3">{item.description}</p>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    onClick={item.onPdf}
                    className="text-sm btn-primary"
                    data-testid={`export-pdf-${item.title.toLowerCase().replace(/[^a-z]/g, '')}`}
                  >
                    <FilePdf weight="bold" size={16} className="mr-2" />
                    PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={item.onCsv}
                    className="text-sm"
                    data-testid={`export-${item.title.toLowerCase().replace(/[^a-z]/g, '')}`}
                  >
                    <FileArrowDown weight="bold" size={16} className="mr-2" />
                    CSV
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
        <p className="text-sm text-[#525866]">
          <strong>Hinweis:</strong> Alle CSV-Dateien verwenden Semikolon (;) als Trennzeichen und sind 
          für die direkte Nutzung in Microsoft Excel und LibreOffice Calc optimiert.
        </p>
      </div>
    </div>
  );
};

export default Reports;
